import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Box } from "@mui/material"
import { grey } from "@mui/material/colors"
import GridCell from "./GridCell"
import FooterTextField from "./FooterTextField"
import { useModalPartida } from "hooks/modalPartida/useModalPartida"
import i18n from "i18n"
import { checkIfMeasuresAreEmpty } from "utils/functions"
import UnityDropdownMUI from "./UnityDropdownMUI"
import QuantityInputWithToggle from "./QuantityInputWithToggle"

import { UNITS, REDUCTIONS, TOTALS, MEASUREMENTS } from "constants/decimals"
import { useDecimalFormatter } from "hooks/useDecimalFormatter"

const LeftFooterGrid = ({
  unity,
  currency,
  setUnity,
  toggleBenefits,
  setToggleBenefits,
  setEnableEdit,
  enableEdit,
  bancoDePrecios,
  mediciones,
  totalDeTotales,
  details,
  isTender,
}) => {
  const {
    state,
    dispatchQuantity,
    setQuantityAndRecalculate,
    dispatchCost,
    setCostAndRecalculate,
    dispatchTotalCost,
    setTotalCostAndRecalculate,
    dispatchCostReduction,
    setCostReductionAndRecalculate,
  } = useModalPartida()

  const handleToggleClick = () => {
    setToggleBenefits(prev => !prev)
    setEnableEdit(true)
  }

  const isReadOnlyUnity = bancoDePrecios === "BANCO DE PRECIOS REFERENCIA"

  const shouldShowTotal = useMemo(() => {
    return mediciones.length > 0 && !checkIfMeasuresAreEmpty(mediciones)
  }, [mediciones])

  const { getDecimals } = useDecimalFormatter()

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gridTemplateRows: "1fr 2fr 2fr",
        width: "100%",
        gap: "4px",
        paddingLeft: "4px",
        paddingRight: "2px",
        paddingBlock: "4px",
        backgroundColor: "#F6F7FB",
        borderBottomLeftRadius: "10px",
      }}
    >
      <GridCell col="1 / 3" row="1 / 2" />
      <GridCell col="3 / 5" row="1 / 2">
        {i18n.t("footerGrid.targetCost")}
      </GridCell>

      <GridCell col="1 / 2" row="2">
        <QuantityInputWithToggle
          toggleBenefits={toggleBenefits}
          decimalScale={getDecimals(MEASUREMENTS)}
          handleToggleClick={handleToggleClick}
          value={
            shouldShowTotal && enableEdit
              ? totalDeTotales
              : state.leftGridContent.quantity
          }
          disabled={mediciones.length > 0 || !!bancoDePrecios || isTender}
          onChange={dispatchQuantity}
          onBlur={setQuantityAndRecalculate}
          label={i18n.t("footerGrid.measurements")}
          sx={{
            opacity: !!bancoDePrecios ? 0.2 : 1,
          }}
        />
      </GridCell>

      <GridCell col="2 / 3" row="2">
        <UnityDropdownMUI
          label={i18n.t("footerGrid.unit")}
          unity={unity}
          setUnity={setUnity}
          disabled={isReadOnlyUnity}
        />
      </GridCell>

      <GridCell col="3 / 4" row="2">
        <FooterTextField
          label={i18n.t("footerGrid.unitCost")}
          value={state.leftGridContent.cost}
          decimalScale={getDecimals(UNITS)}
          onChange={dispatchCost}
          onBlur={value => setCostAndRecalculate(value)}
          disabled={isReadOnlyUnity || details.length > 0 || isTender}
          suffix={currency}
        />
      </GridCell>

      <GridCell col="4 / 5" row="2">
        <FooterTextField
          label={i18n.t("footerGrid.totalCost")}
          value={state.leftGridContent.totalCost}
          decimalScale={getDecimals(TOTALS)}
          onChange={dispatchTotalCost}
          onBlur={value => setTotalCostAndRecalculate(value)}
          disabled={!!bancoDePrecios || details.length > 0 || isTender}
          suffix={currency}
          sx={{
            opacity: !!bancoDePrecios ? 0.2 : 1,
          }}
        />
      </GridCell>

      <GridCell col="1 / 2" row="3">
        <FooterTextField
          label={i18n.t("footerGrid.costReductionPercent")}
          value={state.leftGridContent.costReduction}
          decimalScale={getDecimals(REDUCTIONS)}
          onChange={dispatchCostReduction}
          onBlur={value => setCostReductionAndRecalculate(value)}
          suffix="%"
          disabled={!!bancoDePrecios}
          sx={{
            borderBottomLeftRadius: "10px",
            opacity: !!bancoDePrecios ? 0.2 : 1,
          }}
        />
      </GridCell>

      <GridCell col="2 / 3" row="3">
        <FooterTextField
          label={i18n.t("footerGrid.decrease")}
          value={state.leftGridContent.euroReduced}
          decimalScale={getDecimals(UNITS)}
          disabled
          suffix={currency}
          sx={{
            opacity: !!bancoDePrecios ? 0.2 : 1,
          }}
        />
      </GridCell>

      <GridCell col="3 / 4" row="3">
        <FooterTextField
          label={i18n.t("footerGrid.reducedUnitPrice")}
          value={state.leftGridContent.priceReduced}
          decimalScale={getDecimals(UNITS)}
          suffix={currency}
          disabled
          sx={{
            opacity: !!bancoDePrecios ? 0.2 : 1,
          }}
        />
      </GridCell>

      <GridCell col="4 / 5" row="3">
        <FooterTextField
          label={i18n.t("footerGrid.reducedTotal")}
          disabled
          value={state.leftGridContent.totalReduced}
          decimalScale={getDecimals(TOTALS)}
          suffix={currency}
          sx={{
            opacity: !!bancoDePrecios ? 0.2 : 1,
          }}
        />
      </GridCell>
    </Box>
  )
}

LeftFooterGrid.propTypes = {
  unity: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  setUnity: PropTypes.func.isRequired,
  toggleBenefits: PropTypes.bool.isRequired,
  setToggleBenefits: PropTypes.func.isRequired,
  enableEdit: PropTypes.bool.isRequired,
  setEnableEdit: PropTypes.func.isRequired,
  bancoDePrecios: PropTypes.string,
  mediciones: PropTypes.array.isRequired,
  details: PropTypes.array.isRequired,
  totalDeTotales: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isTender: PropTypes.bool,
}

LeftFooterGrid.defaultProps = {
  mediciones: [],
  enableEdit: false,
}

export default LeftFooterGrid
